import {
  ConsoleOutputLevel,
  DepID_ClientScheduleApplicationEventsBeaconInterval,
  DepID_ClientScheduleMeasurementSchedulerRunInterval,
  DepID_ClientScheduleSessionConfigurationRefreshInterval,
  OutputToConsoleFunc,
} from "../@types"
import { ClientInitializationInfo } from "./client"
import { RuntimeValues } from "./runtimeValues"
import { SessionConfig } from "./sessionConfig"

export interface IntervalManager {
  scheduleSessionRefresh(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
    clientInfo: ClientInitializationInfo,
  ): void

  scheduleBeaconInterval(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
  ): void

  scheduleSchedulerRuns(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
  ): void
}

interface IntervalDependencies {
  callSetInterval: (depID: number, fn: () => void, delay: number) => number
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearInterval: (depID: number, intervalID: number) => void
  outputToConsole: OutputToConsoleFunc
  measurementScheduler: { run(): void }
  triggerSessionRefresh: (clientInfo: ClientInitializationInfo) => void
  triggerBeacon: () => void
}

export function createIntervalManager(
  dependencies: IntervalDependencies,
): IntervalManager {
  return new IntervalManagerImpl(dependencies)
}

class IntervalManagerImpl implements IntervalManager {
  constructor(private dependencies: IntervalDependencies) {}

  scheduleSessionRefresh(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
    clientInfo: ClientInitializationInfo,
  ): void {
    const interval =
      sessionConfig.siteOwnerSettings.sessionRefreshIntervalInMilliseconds

    if (!isNaN(interval)) {
      if (interval > 0) {
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.debug,
          () => `Session refresh interval ${interval} ms`,
        )
        runtimeValues.sessionRefreshDelayInMilliseconds = interval
        runtimeValues.sessionConfigRefreshIntervalID =
          this.dependencies.callSetInterval(
            DepID_ClientScheduleSessionConfigurationRefreshInterval,
            () => this.dependencies.triggerSessionRefresh(clientInfo), // Use injected function
            interval,
          )
      } else if (interval === 0) {
        runtimeValues.sessionRefreshDelayInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.info,
          () => "Session configuration refresh disabled (interval=0)",
        )
      } else {
        runtimeValues.sessionRefreshDelayInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.warn,
          () => `Invalid session config refresh interval: ${interval}`,
        )
      }
    }
  }

  scheduleBeaconInterval(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
  ): void {
    const interval =
      sessionConfig.siteOwnerSettings
        .applicationEventsBeaconIntervalInMilliseconds

    if (!isNaN(interval)) {
      if (interval > 0) {
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.debug,
          () => `Application events beacon interval ${interval} ms`,
        )
        runtimeValues.applicationEventsBeaconIntervalInMilliseconds = interval
        runtimeValues.applicationEventsBeaconIntervalID =
          this.dependencies.callSetInterval(
            DepID_ClientScheduleApplicationEventsBeaconInterval,
            () => this.dependencies.triggerBeacon(), // Use injected function
            interval,
          )
      } else if (interval === 0) {
        runtimeValues.applicationEventsBeaconIntervalInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.info,
          () => "Application events beacon interval disabled (interval=0)",
        )
      } else {
        runtimeValues.applicationEventsBeaconIntervalInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.warn,
          () => `Invalid application events beacon interval: ${interval}`,
        )
      }
    }
  }

  scheduleSchedulerRuns(
    runtimeValues: RuntimeValues,
    sessionConfig: SessionConfig,
  ): void {
    const interval =
      sessionConfig.siteOwnerSettings.schedulerRunIntervalInMilliseconds

    if (!isNaN(interval)) {
      if (interval > 0) {
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.debug,
          () => `Scheduler run interval ${interval} ms`,
        )
        runtimeValues.schedulerRunIntervalInMilliseconds = interval

        // Apply consistent 2-second stagger for coordination
        this.dependencies.callSetTimeout(
          DepID_ClientScheduleMeasurementSchedulerRunInterval,
          () => {
            runtimeValues.schedulerRunIntervalID =
              this.dependencies.callSetInterval(
                DepID_ClientScheduleMeasurementSchedulerRunInterval,
                () => this.dependencies.measurementScheduler.run(),
                interval,
              )
          },
          2000, // Consistent stagger pattern
        )
      } else if (interval === 0) {
        runtimeValues.schedulerRunIntervalInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.info,
          () => "Measurement scheduler disabled (interval=0)",
        )
      } else {
        runtimeValues.schedulerRunIntervalInMilliseconds = 0
        this.dependencies.outputToConsole(
          ConsoleOutputLevel.warn,
          () => `Invalid scheduler run interval: ${interval}`,
        )
      }
    }
  }
}
